import { useCallback, useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: HTMLElement, params: object) => number;
      execute: (widgetId: number) => void;
      reset: (widgetId: number) => void;
    };
    dmV2RecaptchaOnload?: () => void;
  }
}

const SCRIPT_ID = 'dm-v2-recaptcha';
const ONLOAD_CALLBACK = 'dmV2RecaptchaOnload';
// Served from www.recaptcha.net, not www.google.com. Google publishes recaptcha.net as a
// drop-in alternative host for exactly this reason: consent managers (we use CookieYes)
// auto-block anything under the google.com domain as a Google tracker, so when a visitor
// opts out of cookies — the default in Safari and some other browsers — the api.js request
// is blocked before it runs and reCAPTCHA never loads, leaving the form unusable. Loading
// from recaptcha.net keeps every reCAPTCHA request off google.com, so CookieYes leaves it
// alone. reCAPTCHA uses this host for all of its own subsequent requests as well.
//
// `onload=…&render=explicit` is what makes this reliable: Google runs the callback only
// once the API and all its sub-resources are ready, so `grecaptcha.render` is guaranteed
// to exist by then. The bare script-tag `load` event fires earlier — while api.js is
// still pulling those resources in — so rendering off it left the widget unbuilt and the
// form threw "reCAPTCHA is not ready" on submit. The legacy base template uses this same
// onload hook.
const SCRIPT_SRC = `https://www.recaptcha.net/recaptcha/api.js?onload=${ONLOAD_CALLBACK}&render=explicit`;

// Shared across every mount on the page: the script and its onload callback load once,
// and each widget queues a render that fires as soon as the API is ready (or immediately
// if it already is).
let recaptchaReady = false;
const pendingRenders: Array<() => void> = [];

function isRecaptchaReady(): boolean {
  return recaptchaReady || typeof window.grecaptcha?.render === 'function';
}

/**
 * Runs `render` once the reCAPTCHA API is ready, then returns an unsubscribe that removes
 * it from the queue if the component unmounts first. Loads the script on first call.
 */
function whenRecaptchaReady(render: () => void): () => void {
  if (isRecaptchaReady()) {
    render();

    return () => {};
  }

  pendingRenders.push(render);

  if (!document.getElementById(SCRIPT_ID)) {
    window[ONLOAD_CALLBACK] = () => {
      recaptchaReady = true;
      pendingRenders.splice(0).forEach((cb) => cb());
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  }

  return () => {
    const index = pendingRenders.indexOf(render);

    if (index !== -1) {
      pendingRenders.splice(index, 1);
    }
  };
}

/*
 * One key for every brand and every region, and a reCAPTCHA site key is public by
 * design — it ships in the markup of any page using it. Hardcoded here rather than
 * passed in because there is only ever one answer, and a parameter would invite each
 * caller to have an opinion about something it cannot usefully differ on.
 */
const SITE_KEY = '6LeD9qgUAAAAAHWjqkujLxS0tAcWtC8RTQG39qcR';

/**
 * An invisible reCAPTCHA v2 widget.
 *
 * It loads the script itself rather than waiting on a `recaptchaLoaded` event, which is
 * what the legacy version did — that event is fired by the legacy base template, and a
 * V2 page does not include it. Loading it here also means only pages that actually have
 * a protected form pay for the request.
 *
 * In development it resolves a stub token without loading anything: the site key is
 * bound to production hostnames, so a real widget would fail locally regardless.
 */
export default function useRecaptcha() {
  const widgetId = useRef<number | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const pending = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const isDev = process.env.NODE_ENV !== 'production';

  useEffect(() => {
    if (isDev) {
      return;
    }

    // The container must stay renderable — NOT display:none. reCAPTCHA mounts both its
    // badge and, when a challenge is required, the challenge overlay inside this element;
    // hiding the container with display:none hides the challenge too, so execute() can
    // never resolve and the form hangs. The badge itself is hidden globally in v2.css
    // (.grecaptcha-badge), the same way the legacy site does it, which leaves the
    // challenge overlay — a separate sibling element — free to appear.
    const element = document.createElement('div');
    document.body.appendChild(element);
    container.current = element;

    const render = () => {
      if (widgetId.current !== null || !container.current || !window.grecaptcha) {
        return;
      }

      // error-/expired-callback settle the pending promise too: without them, a network
      // hiccup fetching the challenge or a token that expires before submit would leave
      // the promise unresolved and the form stuck on "Sending…" with no error shown.
      const settle = (outcome: () => void) => {
        outcome();
        pending.current = null;
        window.grecaptcha?.reset(widgetId.current!);
      };

      widgetId.current = window.grecaptcha.render(container.current, {
        sitekey: SITE_KEY,
        size: 'invisible',
        callback: (token: string) => settle(() => pending.current?.resolve(token)),
        'error-callback': () =>
          settle(() => pending.current?.reject(new Error('reCAPTCHA error'))),
        'expired-callback': () =>
          settle(() => pending.current?.reject(new Error('reCAPTCHA expired'))),
      });
    };

    const unsubscribe = whenRecaptchaReady(render);

    return () => {
      unsubscribe();

      if (container.current) {
        document.body.removeChild(container.current);
        container.current = null;
      }
    };
  }, [isDev]);

  const executeRecaptcha = useCallback((): Promise<string> => {
    if (isDev) {
      return Promise.resolve('dev-token');
    }

    return new Promise((resolve, reject) => {
      if (widgetId.current === null || !window.grecaptcha) {
        reject(new Error('reCAPTCHA is not ready'));

        return;
      }

      pending.current = { resolve, reject };
      window.grecaptcha.execute(widgetId.current);
    });
  }, [isDev]);

  return { executeRecaptcha };
}
